import { motion } from "framer-motion";
import { DB } from "@/lib/media";
import { WHATSAPP_HREF } from "@/lib/links";
import { trackWhatsAppClick } from "@/lib/analytics";

const ease = [0.22, 1, 0.36, 1];

const PILLARS = [
    {
        label: "Go Live Faster",
        body: "Rapid onboarding across quick commerce & marketplace platforms.",
    },
    {
        label: "List Across Platforms",
        body: "Blinkit, Zepto, Instamart, Amazon, Flipkart & more.",
    },
    {
        label: "Drive Sales Everywhere",
        body: "Full-funnel media + creative built for velocity.",
    },
    {
        label: "Scale Sustainably",
        body: "Data, cataloging & margin discipline that compound.",
    },
];

export default function QuickCommerce() {
    return (
        <section
            id="commerce"
            data-testid="quick-commerce-section"
            className="relative py-24 lg:py-32 bg-[#F5EFE1] overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                {/* Header row */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 lg:mb-20">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                            (Capability / 03)
                        </div>
                        <h2
                            data-testid="quick-commerce-heading"
                            className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl"
                        >
                            Launch. List.{" "}
                            <span className="text-olive">Scale.</span>
                        </h2>
                    </div>
                    <p className="font-serif italic text-xl lg:text-2xl text-black/65 max-w-md">
                        Helping brands grow across Quick Commerce &amp;
                        Marketplaces — end-to-end.
                    </p>
                </div>

                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10 items-start">
                    {/* Illustration / banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, ease }}
                        className="col-span-12 lg:col-span-7 relative"
                    >
                        <div className="relative overflow-hidden bg-white shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)]">
                            <img
                                src={DB.quickCommerce}
                                alt="Digitalbeej — Launch. List. Scale. Quick Commerce & Marketplaces"
                                className="w-full h-auto object-cover"
                                loading="lazy"
                            />
                        </div>

                        {/* Bottom capability strip */}
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[10px] uppercase tracking-[0.16em] font-bold text-black/70">
                            {[
                                "End-to-End Onboarding",
                                "Cataloging & Optimization",
                                "Performance Marketing",
                                "Data-Driven Growth",
                            ].map((c) => (
                                <div
                                    key={c}
                                    className="flex items-start gap-2 py-2 border-t border-black/20"
                                >
                                    <span className="inline-block w-2 h-2 bg-olive mt-1" />
                                    {c}
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Pillars */}
                    <div className="col-span-12 lg:col-span-5 lg:pl-6">
                        <ul className="divide-y divide-black/15 border-y border-black/15">
                            {PILLARS.map((p, i) => (
                                <motion.li
                                    key={p.label}
                                    data-testid={`commerce-pillar-${i}`}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{
                                        duration: 0.7,
                                        ease,
                                        delay: i * 0.08,
                                    }}
                                    className="py-5 flex items-start gap-5 group"
                                >
                                    <span className="font-display font-black text-2xl text-olive leading-none w-10 shrink-0 pt-1">
                                        0{i + 1}
                                    </span>
                                    <div>
                                        <div className="font-display font-bold text-lg lg:text-xl tracking-tight">
                                            {p.label}
                                        </div>
                                        <div className="mt-1 text-sm lg:text-base text-black/65 leading-relaxed">
                                            {p.body}
                                        </div>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>

                        <div className="mt-8 flex flex-wrap items-center gap-6">
                            <a
                                data-testid="commerce-cta-whatsapp"
                                href={WHATSAPP_HREF}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() =>
                                    trackWhatsAppClick({
                                        section: "commerce",
                                        buttonName: "commerce_whatsapp",
                                    })
                                }
                                className="group inline-flex items-center gap-3 bg-obsidian text-bone px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive hover:text-obsidian transition-colors duration-300"
                            >
                                Talk commerce
                                <span
                                    aria-hidden
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </a>
                            <a
                                href="#work"
                                className="text-xs uppercase tracking-[0.2em] font-bold link-underline"
                            >
                                See commerce work
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
